import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  AlertCircle,
  PenTool,
  Type,
  Eraser,
  Download
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const AgreementSignPage = () => {
  const { agreementId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [agreement, setAgreement] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [existingSignature, setExistingSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  
  // Signature state
  const [signatureType, setSignatureType] = useState("typed"); // "typed" or "drawn"
  const [typedSignature, setTypedSignature] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Canvas drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    fetchAgreement();
    if (user) {
      checkIfSigned();
    }
  }, [agreementId, user]);

  useEffect(() => {
    if (signatureType === "drawn" && canvasRef.current) {
      initCanvas();
    }
  }, [signatureType]);

  const fetchAgreement = async () => {
    try {
      const response = await fetch(`${API}/public/agreements/${agreementId}`);
      if (response.ok) {
        const data = await response.json();
        setAgreement(data);
      } else {
        toast.error("Agreement not found");
      }
    } catch (error) {
      toast.error("Failed to load agreement");
    } finally {
      setLoading(false);
    }
  };

  const checkIfSigned = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/merchant/agreements/${agreementId}/check-signed`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setAlreadySigned(data.signed);
        setExistingSignature(data.signature);
      }
    } catch (error) {
      console.error("Error checking signature:", error);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    initCanvas();
    setHasDrawn(false);
  };

  const getCanvasData = () => {
    const canvas = canvasRef.current;
    return canvas ? canvas.toDataURL("image/png") : null;
  };

  const handleSign = async () => {
    if (!user) {
      toast.error("Please login to sign the agreement");
      navigate(`/login?redirect=/agreement/${agreementId}`);
      return;
    }

    if (!agreedToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    let signatureData = "";
    if (signatureType === "typed") {
      if (!typedSignature.trim()) {
        toast.error("Please type your signature");
        return;
      }
      signatureData = typedSignature;
    } else {
      if (!hasDrawn) {
        toast.error("Please draw your signature");
        return;
      }
      signatureData = getCanvasData();
    }

    setSigning(true);
    try {
      const response = await fetch(`${API}/merchant/agreements/${agreementId}/sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          signature_data: signatureData,
          signature_type: signatureType
        })
      });

      if (response.ok) {
        toast.success("Agreement signed successfully!");
        setAlreadySigned(true);
        checkIfSigned();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to sign agreement");
      }
    } catch (error) {
      toast.error("Failed to sign agreement");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-red-200 p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Agreement Not Found</h2>
          <p className="text-slate-500 mb-6">This agreement doesn't exist or has been removed.</p>
          <Link to="/">
            <Button className="rounded-full">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto" />
              <div className="h-6 w-px bg-slate-300" />
              <span className="font-brand text-sm font-bold text-slate-900">VEYRONIX</span>
            </div>
            <span className="text-sm text-slate-500">Digital Agreement</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {alreadySigned ? (
          /* Already Signed View */
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Agreement Already Signed
              </h1>
              <p className="text-slate-500 mb-6">
                You signed this agreement on {existingSignature?.signed_at ? 
                  new Date(existingSignature.signed_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  }) : 'a previous date'}.
              </p>
              <div className="bg-slate-50 rounded-xl p-4 text-left mb-6">
                <p className="text-sm text-slate-600"><strong>Product:</strong> {agreement.product?.name}</p>
                <p className="text-sm text-slate-600"><strong>Merchant:</strong> {agreement.merchant?.name}</p>
                <p className="text-sm text-slate-600"><strong>Signature ID:</strong> {existingSignature?.signature_id}</p>
              </div>
              <Link to="/dashboard">
                <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          /* Signing Form */
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Agreement Content */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-slate-900">
                      {agreement.product?.name || "Subscription Agreement"}
                    </h2>
                    <p className="text-sm text-slate-500">by {agreement.merchant?.name}</p>
                  </div>
                </div>

                {agreement.product && (
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-slate-600">
                      <strong>Price:</strong> £{agreement.product.subscription_price || agreement.product.price}
                      {agreement.product.subscription_interval && ` / ${agreement.product.subscription_interval}`}
                    </p>
                  </div>
                )}

                <div className="border-t border-slate-200 pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Terms & Conditions</h3>
                  <div className="prose prose-sm max-w-none text-slate-600 max-h-64 overflow-y-auto">
                    <p className="whitespace-pre-wrap">{agreement.terms}</p>
                  </div>
                </div>

                {agreement.penalty_terms && (
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="font-semibold text-amber-700 mb-2">Penalty Terms</h3>
                    <p className="text-sm text-amber-600">{agreement.penalty_terms}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Signature Section */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-4">Your Signature</h3>
                
                {/* Signature Type Toggle */}
                <div className="flex gap-2 mb-6">
                  <Button
                    type="button"
                    variant={signatureType === "typed" ? "default" : "outline"}
                    onClick={() => setSignatureType("typed")}
                    className="flex-1 rounded-full"
                    data-testid="typed-signature-btn"
                  >
                    <Type className="h-4 w-4 mr-2" />
                    Type
                  </Button>
                  <Button
                    type="button"
                    variant={signatureType === "drawn" ? "default" : "outline"}
                    onClick={() => setSignatureType("drawn")}
                    className="flex-1 rounded-full"
                    data-testid="drawn-signature-btn"
                  >
                    <PenTool className="h-4 w-4 mr-2" />
                    Draw
                  </Button>
                </div>

                {signatureType === "typed" ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Type your full name</Label>
                      <Input
                        value={typedSignature}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder="Your full legal name"
                        className="mt-1"
                        data-testid="typed-signature-input"
                      />
                    </div>
                    {typedSignature && (
                      <div className="p-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                        <p className="text-center font-signature text-2xl text-slate-700 italic">
                          {typedSignature}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Draw your signature</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearCanvas}
                        className="text-slate-500"
                      >
                        <Eraser className="h-4 w-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full cursor-crosshair touch-none"
                        data-testid="signature-canvas"
                      />
                    </div>
                    <p className="text-xs text-slate-400 text-center">
                      Use your mouse or finger to draw your signature
                    </p>
                  </div>
                )}

                {/* Agreement Checkbox */}
                <div className="flex items-start gap-3 mt-6 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="agree"
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                    data-testid="agree-checkbox"
                  />
                  <label htmlFor="agree" className="text-sm text-slate-600 cursor-pointer">
                    I have read and agree to the terms and conditions above. I understand that this
                    is a legally binding agreement.
                  </label>
                </div>

                {/* Sign Button */}
                {user ? (
                  <Button
                    onClick={handleSign}
                    disabled={signing || !agreedToTerms}
                    className="w-full mt-6 h-12 bg-indigo-600 hover:bg-indigo-700 rounded-full"
                    data-testid="sign-agreement-btn"
                  >
                    {signing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing...
                      </div>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Sign Agreement
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="mt-6 text-center">
                    <p className="text-slate-500 mb-3">Please login to sign this agreement</p>
                    <Link to={`/login?redirect=/agreement/${agreementId}`}>
                      <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                        Login to Sign
                      </Button>
                    </Link>
                  </div>
                )}

                {user && (
                  <p className="text-xs text-slate-400 text-center mt-4">
                    Signing as: {user.name} ({user.email})
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgreementSignPage;
